/* ============================================================================
   FRONT LINE DEV - DO NOT EDIT
   This file powers Infinit's custom Quiz and Custom Formula functionality and
   is maintained exclusively by Front Line Dev. Editing it may break the
   formula builder, quiz, saved formulas, or related features. Please contact
   Front Line Dev for any changes.
   ============================================================================ */

/*
  Quiz navigation script (Alchemy theme)
  - Navigates through quiz panels in DOM order (one question per panel).
  - Panels can share the same data-step (phase).
  - Dependent panels use:
      data-depends-on="input_name"
      data-depends-values="value1|value2"
    (The panel is considered unavailable unless the controlling answer matches.)
*/

(function () {
  const root = document.getElementById('infinit-quiz');
  if (!root) return;

  const panels = Array.from(root.querySelectorAll('.quiz__panel'));
  const stepBtns = Array.from(root.querySelectorAll('[data-step-nav]'));
  const stepsList = root.querySelector('.quiz__steps-list');
  const backBtn = document.getElementById('QuizBack');
  const nextBtn = document.getElementById('QuizNext');
  const summaryEl = document.getElementById('QuizSummary');
  const startBtn = root.querySelector('[data-quiz-start]');

  // ADD THESE:
  const themeOverlay = document.querySelector('.js-overlay');
  let overlayWasVisible = false;
  let workingNode = null;

  function ensureWorkingNode() {
    if (!themeOverlay) return null;
    if (workingNode) return workingNode;

    workingNode = document.createElement('div');
    workingNode.className =
      'quiz-working-overlay';
    workingNode.setAttribute('role', 'status');
    workingNode.setAttribute('aria-live', 'polite');

    const kicker = document.createElement('p');
    kicker.className = 'quiz-working-overlay__text h6 uppercase tracking-wider mb-2';
    kicker.textContent = 'INFINIT FORMULA FINDER';

    const h = document.createElement('h2');
    h.className = 'quiz-working-overlay__heading h2 uppercase leading-none m-0';
    h.textContent = 'Building Your Custom Plan';

    workingNode.appendChild(kicker);
    workingNode.appendChild(h);

    themeOverlay.appendChild(workingNode);
    return workingNode;
  }

  function showQuizWorkingOverlay() {
    if (!themeOverlay) return;
    ensureWorkingNode();

    overlayWasVisible = themeOverlay.classList.contains('is-visible');
    themeOverlay.classList.add('is-visible', 'quiz-overlay--working');
    root.setAttribute('aria-busy', 'true');
  }

  function hideQuizWorkingOverlay() {
    if (!themeOverlay) return;

    themeOverlay.classList.remove('quiz-overlay--working');
    if (!overlayWasVisible) themeOverlay.classList.remove('is-visible');

    root.removeAttribute('aria-busy');
  }

  function isPlanLockPanel(panel) {
    return !!panel && panel.dataset.panel === 'plan-lock';
  }


  if (!panels.length || !backBtn || !nextBtn) return;
  
  syncStepNavGradientFromAnnouncement();


  let activeIndex = 0;
  let visitedMaxStep = 0;

  function panelStep(panel) {
    return Number(panel?.dataset?.step || 0);
  }

  function isPanelAvailable(panel) {
    return panel && panel.dataset.depInactive !== '1';
  }

  function syncIntroState() {
    const step = panelStep(panels[activeIndex]);
    root.classList.toggle('is-intro', step < 0);
  }


  function findFirstAvailableIndex() {
    for (let i = 0; i < panels.length; i++) {
      if (isPanelAvailable(panels[i])) return i;
    }
    return 0;
  }

  function findNextAvailableIndex(fromIndex) {
    for (let i = fromIndex + 1; i < panels.length; i++) {
      if (isPanelAvailable(panels[i])) return i;
    }
    return -1;
  }

  function findPrevAvailableIndex(fromIndex) {
    for (let i = fromIndex - 1; i >= 0; i--) {
      if (isPanelAvailable(panels[i])) return i;
    }
    return -1;
  }

  function firstAvailableIndexForStep(step) {
    for (let i = 0; i < panels.length; i++) {
      if (panelStep(panels[i]) === step && isPanelAvailable(panels[i])) return i;
    }
    return -1;
  }

  function hasAnyPanel(step) {
    return panels.some(p => panelStep(p) === step);
  }

  function getControlValues(name) {
    if (!name) return [];

    const r = root.querySelector(`input[type="radio"][name="${name}"]:checked`);
    if (r) return [r.value];

    const cbs = root.querySelectorAll(
      `input[type="checkbox"][name="${name}"]:checked, input[type="checkbox"][name="${name}[]"]:checked`
    );
    if (cbs && cbs.length) return Array.from(cbs).map(el => el.value);

    const range = root.querySelector(`input[type="range"][name="${name}"]`);
    if (range) return [range.value];

    const text = root.querySelector(
      `input[name="${name}"]:not([type]), input[type="text"][name="${name}"], input[type="email"][name="${name}"], input[type="date"][name="${name}"], input[type="number"][name="${name}"], textarea[name="${name}"]`
    );

    if (text) return [(text.value || '').trim()];

    return [];
  }

  function clearPanelInputs(panel) {
    if (!panel) return;

    const inputs = Array.from(panel.querySelectorAll('input, textarea, select'));
    inputs.forEach(inp => {
      const t = (inp.type || '').toLowerCase();

      if (t === 'radio' || t === 'checkbox') {
        inp.checked = false;
        if (t === 'checkbox') {
          inp.disabled = false;
          inp.removeAttribute('aria-disabled');
        }
      } else if (t === 'range') {
        const vAttr = inp.getAttribute('value');
        const nextVal = vAttr !== null ? vAttr : (inp.min || 0);
        inp.value = nextVal;
        delete inp.dataset.quizTouched;
      } else {
        inp.value = '';
      }
    });
  }

  function handleExclusiveCheckboxChange(target) {
    if (!(target instanceof HTMLInputElement)) return;
    if (target.type !== 'checkbox') return;

    const panel = target.closest('.quiz__panel');
    if (!panel) return;

    const raw = (panel.dataset.exclusiveValues || '').trim();
    if (!raw) return;

    const exclusiveSet = new Set(
      raw.split('|').map(s => (s || '').trim()).filter(Boolean)
    );
    if (!exclusiveSet.size) return;

    const all = Array.from(panel.querySelectorAll('input[type="checkbox"]'));
    if (!all.length) return;

    const exclusives = all.filter(c => exclusiveSet.has(c.value));
    const isExclusive = exclusiveSet.has(target.value);

    // Exclusive checked -> disable + uncheck everything else
    if (isExclusive && target.checked) {
      all.forEach(c => {
        if (c === target) {
          c.disabled = false;
          c.removeAttribute('aria-disabled');
        } else {
          c.checked = false;
          c.disabled = true;
          c.setAttribute('aria-disabled', 'true');
        }
      });
      return;
    }

    // Exclusive unchecked -> re-enable everything
    if (isExclusive && !target.checked) {
      all.forEach(c => {
        c.disabled = false;
        c.removeAttribute('aria-disabled');
      });
      return;
    }

    // Non-exclusive checked -> ensure exclusives are unchecked and everything stays enabled
    if (!isExclusive && target.checked) {
      exclusives.forEach(ex => { ex.checked = false; });
      all.forEach(c => {
        c.disabled = false;
        c.removeAttribute('aria-disabled');
      });
    }
  }


  function evaluateDependencies() {
    panels.forEach(panel => {
      const dependsOn = panel.dataset.dependsOn;
      const dependsValues = panel.dataset.dependsValues;

      if (!dependsOn || !dependsValues) return;

      const wanted = dependsValues
        .split('|')
        .map(s => (s || '').trim())
        .filter(Boolean);

      const actual = getControlValues(dependsOn);
      const match = wanted.some(w => actual.includes(w));

      if (!match) {
        if (panel.dataset.depInactive !== '1') {
          clearPanelInputs(panel);
        }
        panel.dataset.depInactive = '1';
      } else {
        delete panel.dataset.depInactive;
      }
    });

    if (!isPanelAvailable(panels[activeIndex])) {
      const prev = findPrevAvailableIndex(activeIndex);
      const next = findNextAvailableIndex(activeIndex);
      if (prev !== -1) {
        setActiveIndex(prev);
      } else if (next !== -1) {
        setActiveIndex(next);
      } else {
        setActiveIndex(findFirstAvailableIndex());
      }
    }

    updateStepNav();
    updateNextState();
  }

  function isPanelAnswered(panel) {
    if (!panel) return false;
    if (panel.dataset.allowEmpty === 'true') return true;

    const radios = Array.from(panel.querySelectorAll('input[type="radio"]'));
    const radioNames = Array.from(new Set(radios.map(r => r.name).filter(Boolean)));
    for (const rn of radioNames) {
      if (!panel.querySelector(`input[type="radio"][name="${rn}"]:checked`)) return false;
    }

    const checkboxes = Array.from(panel.querySelectorAll('input[type="checkbox"]'));
    if (checkboxes.length) {
      // If any checkbox is marked required, enforce that all required ones are checked.
      const requiredCbs = checkboxes.filter(c => c.required || c.getAttribute('aria-required') === 'true');

      if (requiredCbs.length) {
        const allRequiredChecked = requiredCbs.every(c => c.checked);
        if (!allRequiredChecked) return false;
      } else {
        // Fallback: panels that use checkbox questions can still use minChecks
        const minChecks = Number(panel.dataset.minChecks || 1);
        const checkedCount = checkboxes.filter(c => c.checked).length;
        if (checkedCount < minChecks) return false;
      }
    }

    const ranges = Array.from(panel.querySelectorAll('input[type="range"]'));
    if (ranges.length) {
      // Default behavior: a slider has a default value, so it's considered answered immediately.
      // If you ever want to require user interaction for a specific slider panel, add:
      //   data-range-require-touch="true"
      const requireTouch = panel.dataset.rangeRequireTouch === 'true';

      if (requireTouch) {
        const anyTouched = ranges.some(
          r => r.dataset.quizTouched === '1' || r.dataset.quizTouched === 'true'
        );
        if (!anyTouched) return false;
      }
    }

    const texts = Array.from(panel.querySelectorAll('input[type="text"], input[type="email"], input[type="date"], input[type="number"], textarea'));

    if (texts.length) {
      const optional = panel.dataset.textOptional === 'true';
      if (!optional) {
        const requiredFields = texts.filter(t => t.required || t.getAttribute('aria-required') === 'true');

        if (requiredFields.length) {
          const allValid = requiredFields.every(t =>
            (t.value || '').trim().length && (!t.validity || t.validity.valid)
          );
          if (!allValid) return false;
        } else {
          // No required text/date/number fields.
          // If the panel also has radios/checkboxes/sliders, treat these inputs as optional.
          const hasOtherAnswerType = !!panel.querySelector('input[type="radio"], input[type="checkbox"], input[type="range"]');

          if (!hasOtherAnswerType) {
            // Text-only panel -> require something filled
            const anyFilled = texts.some(t => (t.value || '').trim().length);
            if (!anyFilled) return false;
          }

          // If user typed something, still respect native validity (min/max/date max, etc.)
          const anyInvalid = texts.some(t =>
            (t.value || '').trim().length && t.validity && !t.validity.valid
          );
          if (anyInvalid) return false;
        }
      }
    }


    return true;
  }

  function updateNextState() {
    const panel = panels[activeIndex];
    const hasNext = findNextAvailableIndex(activeIndex) !== -1;
    const canAdvance = isPanelAnswered(panel) && hasNext;

    nextBtn.disabled = !canAdvance;
    nextBtn.setAttribute('aria-disabled', String(!canAdvance));
  }

    function syncStepNavGradientFromAnnouncement() {
    if (!stepsList) return;

    const ab = document.querySelector('announcement-bar');
    if (!ab) return;

    const cs = getComputedStyle(ab);
    const bg = cs.getPropertyValue('--announcement-bg-color').trim();
    const grad = cs.getPropertyValue('--announcement-bg-gradient').trim();
    const txt = cs.getPropertyValue('--announcement-text-color').trim();

    if (bg) stepsList.style.setProperty('--announcement-bg-color', bg);
    if (grad) stepsList.style.setProperty('--announcement-bg-gradient', grad);
    if (txt) stepsList.style.setProperty('--announcement-text-color', txt);
  }

  function setStepNavProgress(stepIdx) {
    if (!stepsList) return;

    if (stepIdx == null || stepIdx < 0) {
      stepsList.style.setProperty('--steps-progress', '0%');
      return;
    }

    const total = stepBtns.length || 1;
    const idx = Math.max(0, Math.min(stepIdx, total - 1));
    const pct = ((idx + 1) / total) * 100;

    stepsList.style.setProperty('--steps-progress', `${pct}%`);
  }


  function updateStepNav() {
    const currentStep = panelStep(panels[activeIndex]);

    setStepNavProgress(currentStep);

    if (currentStep < 0) {
      stepBtns.forEach(btn => {
        btn.removeAttribute('aria-current');
        btn.disabled = true;
      });
      return;
    }
    visitedMaxStep = Math.max(visitedMaxStep, currentStep);

    stepBtns.forEach(btn => {
      const idx = Number(btn.dataset.stepNav);
      if (idx === currentStep) btn.setAttribute('aria-current', 'step');
      else btn.removeAttribute('aria-current');

      btn.disabled = (idx > visitedMaxStep) || !hasAnyPanel(idx);
    });
  }

  function collectAllAnswers() {
    const answers = {};
    panels.forEach(p => {
      if (!isPanelAvailable(p)) return;

      const checked = Array.from(p.querySelectorAll('input:checked'));
      checked.forEach(input => {
        const rawName = input.name || '';
        const name = rawName.replace(/\[\]$/, '');
        if (!name) return;

        if (rawName.endsWith('[]')) {
          answers[name] = answers[name] || [];
          answers[name].push(input.value);
        } else {
          answers[name] = input.value;
        }
      });

      const ranges = Array.from(p.querySelectorAll('input[type="range"]'));
      ranges.forEach(r => {
        const name = (r.name || '').replace(/\[\]$/, '');
        if (!name) return;
        answers[name] = r.value;
      });

      const texts = Array.from(p.querySelectorAll('input[type="text"], input[type="email"], input[type="date"], input[type="number"], textarea'));
      texts.forEach(t => {
        const name = (t.name || '').replace(/\[\]$/, '');
        if (!name) return;
        if ((t.value || '').trim().length) answers[name] = (t.value || '').trim();
      });
    });

    return answers;
  }

  // Build a human-readable {question, answer} list mirroring panel order.
  // This is stored alongside the raw `answers` object so the My Quizzes
  // modal can render the original prompt text + chosen labels without
  // having to reverse-map input names back to questions.
  function collectAnswersDisplay() {
    const out = [];
    panels.forEach(p => {
      if (!isPanelAvailable(p)) return;
      // Skip intro/welcome panels — they have no answerable inputs.
      const hasInputs = p.querySelector('input, textarea, select');
      if (!hasInputs) return;

      const titleEl = p.querySelector('.quiz__title, .quiz__heading, h1, h2');
      const question = (titleEl ? (titleEl.textContent || '') : '').trim();
      if (!question) return;

      const answerParts = [];

      // Radios + checkboxes: collect the visible label text
      const checked = Array.from(p.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked'));
      checked.forEach(input => {
        // Filter out hidden consent/marketing checkboxes from email panel —
        // we display these separately if needed, but they aren't a question.
        const name = input.name || '';
        if (name.indexOf('customer[') === 0 || name === 'quiz_consent_plan') return;

        let labelText = '';
        if (input.id) {
          const lbl = p.querySelector('label[for="' + CSS.escape(input.id) + '"]');
          if (lbl) labelText = (lbl.textContent || '').trim();
        }
        if (!labelText) labelText = input.value || '';
        if (labelText) answerParts.push(labelText);
      });

      // Range sliders: show numeric value + any visible label (eg. "Intensity 7")
      const ranges = Array.from(p.querySelectorAll('input[type="range"]'));
      ranges.forEach(r => {
        if (!r.value) return;
        let sliderLabel = '';
        if (r.id) {
          const lbl = p.querySelector('label[for="' + CSS.escape(r.id) + '"]');
          if (lbl) sliderLabel = (lbl.textContent || '').trim();
        }
        const v = r.value;
        answerParts.push(sliderLabel ? (sliderLabel + ': ' + v) : v);
      });

      // Text/email/number/date/textarea: use the raw value
      const texts = Array.from(p.querySelectorAll('input[type="text"], input[type="email"], input[type="date"], input[type="number"], textarea'));
      texts.forEach(t => {
        // Skip the customer email/name fields on the final panel — those
        // aren't quiz answers, they're the contact block.
        const name = t.name || '';
        if (name.indexOf('customer[') === 0) return;
        const v = (t.value || '').trim();
        if (v) answerParts.push(v);
      });

      if (answerParts.length === 0) return;
      out.push({ question: question, answer: answerParts.join(', ') });
    });
    return out;
  }

  function renderScoredResults(results) {
    const container = document.getElementById('QuizResults');
    if (!container) return;

    // Heading
    const heading = container.querySelector('[data-quiz-heading]');
    if (heading) heading.textContent = results.firstName + "'s Custom Fueling Plan";

    // Results text
    const textEl = container.querySelector('[data-quiz-results-text]');
    if (textEl) {
      textEl.innerHTML = results.resultsText
        .map(function (t) { return '<p>' + t + '</p>'; })
        .join('');
    }

    // Helper: build detail list for a hydration formula
    function hydrationDetails(formula) {
      var items = [];
      items.push('<li>Flavor: ' + formula.flavor + ' (intensity ' + formula.flavor_slider + ')</li>');
      items.push('<li>Electrolytes: ' + formula.electrolytes_slider + '</li>');
      items.push('<li>Calories: ' + formula.calories_slider + '</li>');
      items.push('<li>Carbs: ' + formula.carbs_slider + '</li>');
      if (formula.caffeine_slider > 0) items.push('<li>Caffeine: ' + formula.caffeine_slider + '</li>');
      if (formula.protein_slider > 0) items.push('<li>Protein: ' + formula.protein_slider + '</li>');
      if (formula.aminoacids_slider > 0) items.push('<li>Amino Acids: ' + formula.aminoacids_slider + '</li>');
      if (formula.boosts && formula.boosts.length) items.push('<li>Boosts: ' + formula.boosts.length + ' added</li>');
      return items.join('');
    }

    // Helper: build detail list for protein formula
    function proteinDetails(formula) {
      var items = [];
      items.push('<li>Flavor: ' + formula.flavor + ' (intensity ' + formula.flavor_slider + ')</li>');
      items.push('<li>Protein Type: ' + formula.protein_type + '</li>');
      var ccLabel = formula.calorie_carbs_slider === 0 ? 'Low Calorie' : formula.calorie_carbs_slider === 100 ? 'High Calorie' : 'Moderate Calorie';
      items.push('<li>Calorie Level: ' + ccLabel + '</li>');
      if (formula.boosts && formula.boosts.length) items.push('<li>Boosts: ' + formula.boosts.length + ' added</li>');
      return items.join('');
    }

    // Helper: build formula link using FormulaUrlHash
    function buildLink(formula, pageType) {
      var basePath = pageType === 'protein' ? '/pages/custom-protein' : '/pages/custom-hydration';
      if (window.FormulaUrlHash) {
        var hash = window.FormulaUrlHash.encode(formula);
        return hash ? basePath + '#' + hash : basePath;
      }
      return basePath;
    }

    // Hydration A
    var cardA = container.querySelector('[data-quiz-formula="hydrationA"]');
    if (cardA) {
      cardA.querySelector('[data-quiz-formula-name]').textContent = results.formulaNames.a;
      cardA.querySelector('[data-quiz-formula-details]').innerHTML = hydrationDetails(results.hydrationA);
      cardA.querySelector('[data-quiz-formula-link]').href = buildLink(results.hydrationA, 'hydration');
    }

    // Hydration B
    var cardB = container.querySelector('[data-quiz-formula="hydrationB"]');
    if (cardB) {
      if (results.hydrationB) {
        cardB.hidden = false;
        cardB.querySelector('[data-quiz-formula-name]').textContent = results.formulaNames.b;
        cardB.querySelector('[data-quiz-formula-details]').innerHTML = hydrationDetails(results.hydrationB);
        cardB.querySelector('[data-quiz-formula-link]').href = buildLink(results.hydrationB, 'hydration');
      } else {
        cardB.hidden = true;
      }
    }

    // Protein
    var cardP = container.querySelector('[data-quiz-formula="protein"]');
    if (cardP) {
      cardP.querySelector('[data-quiz-formula-name]').textContent = results.formulaNames.protein;
      cardP.querySelector('[data-quiz-formula-details]').innerHTML = proteinDetails(results.protein);
      cardP.querySelector('[data-quiz-formula-link]').href = buildLink(results.protein, 'protein');
    }

    // Recommendations
    var recsWrap = container.querySelector('[data-quiz-recs]');
    var recsList = container.querySelector('[data-quiz-recs-list]');
    if (recsWrap && recsList && results.recommendations.length) {
      recsWrap.hidden = false;
      recsList.innerHTML = results.recommendations
        .map(function (r) {
          var text = r.product;
          if (r.variant) text += ' — ' + r.variant;
          return '<li>' + text + '</li>';
        })
        .join('');
    }

    // Show the container
    container.hidden = false;
  }

  function renderSummaryIfNeeded() {
    // No-op: results are now rendered by the scoring engine via renderScoredResults()
  }

  function parseSnapPoints(input) {
    const raw = (input?.dataset?.snap || '').trim();
    if (!raw) return null;
    const pts = raw.split('|').map(s => Number(String(s).trim())).filter(n => !Number.isNaN(n));
    return pts.length ? pts : null;
  }

  function nearestSnap(value, points) {
    let best = points[0];
    let bestDist = Math.abs(value - best);
    for (let i = 1; i < points.length; i++) {
      const d = Math.abs(value - points[i]);
      if (d < bestDist) { best = points[i]; bestDist = d; }
    }
    return best;
  }

  function showOnly(panel) {
    panels.forEach(p => { p.hidden = (p !== panel); });
    syncIntroState();
    updateStepNav();
    updateNextState();
    renderSummaryIfNeeded();
  }

  function setActiveIndex(idx) {
    if (idx < 0 || idx >= panels.length) return;
    if (!isPanelAvailable(panels[idx])) return;

    activeIndex = idx;
    showOnly(panels[activeIndex]);

    backBtn.disabled = findPrevAvailableIndex(activeIndex) === -1;
  }

  function goNext() {
    const next = findNextAvailableIndex(activeIndex);
    if (next === -1) return;
    setActiveIndex(next);
  }

  function goBack() {
    const prev = findPrevAvailableIndex(activeIndex);
    if (prev === -1) return;
    setActiveIndex(prev);
  }

  stepBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const step = Number(btn.dataset.stepNav);
      if (Number.isNaN(step)) return;
      if (step > visitedMaxStep) return;
      if (!hasAnyPanel(step)) return;

      const idx = firstAvailableIndexForStep(step);
      if (idx !== -1) setActiveIndex(idx);
    });
  });

  backBtn.addEventListener('click', goBack);
  nextBtn.addEventListener('click', (e) => {
    const panel = panels[activeIndex];

    // default behavior for all other panels
    if (!isPlanLockPanel(panel)) {
      goNext();
      return;
    }

    // Plan-lock panel: collect answers, score, show overlay, then advance to results
    e.preventDefault();

    // Re-evaluate dependencies to catch any stale state from step-nav jumps,
    // then find the first available panel before this one that still needs an answer.
    evaluateDependencies();
    let firstMissed = -1;
    for (let i = 0; i < activeIndex; i++) {
      if (isPanelAvailable(panels[i]) && !isPlanLockPanel(panels[i]) && !isPanelAnswered(panels[i])) {
        firstMissed = i;
        break;
      }
    }
    if (firstMissed !== -1) {
      setActiveIndex(firstMissed);
      return;
    }

    const next = findNextAvailableIndex(activeIndex);
    if (next === -1) return;

    showQuizWorkingOverlay();

    // prevent interaction during scoring
    nextBtn.disabled = true;
    backBtn.disabled = true;
    stepBtns.forEach(b => (b.disabled = true));

    const answers = collectAllAnswers();
    const minOverlay = new Promise(r => window.setTimeout(r, 5000));

    const scoring = (window.QuizEngine && window.QuizEngine.fetchAndScore)
      ? window.QuizEngine.fetchAndScore(answers)
      : Promise.resolve(null);

    Promise.all([scoring, minOverlay]).then(function (res) {
      const results = res[0];

      if (results) {
        // Render inline as fallback (visible briefly before redirect)
        renderScoredResults(results);

        var scored_at = new Date().toISOString();
        var answersDisplay = collectAnswersDisplay();
        var sessionPayload = {
          answers: answers,
          answers_display: answersDisplay,
          results: results,
          scored_at: scored_at
        };

        // POST to /apps/formulas/quiz/finalize in parallel with the
        // InfinitSession write. Both must finish before we redirect so
        // the customer's submission token can be appended to the URL —
        // that token also keys the deep-link in the Klaviyo email.
        // collectAllAnswers() flattens bracketed input names verbatim,
        // so the customer fields land as `customer[email]` etc.
        var finalizeBody = {
          email: (answers && answers['customer[email]']) || '',
          first_name: (answers && answers['customer[first_name]']) || '',
          last_name: (answers && answers['customer[last_name]']) || '',
          accepts_marketing: !!(answers && answers['customer[accepts_marketing]']),
          payload: sessionPayload
        };
        var finalizePromise = fetch('/apps/formulas/quiz/finalize', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(finalizeBody)
        }).then(function (r) {
          return r.json().catch(function () { return {}; });
        }).catch(function (err) {
          console.warn('[quiz] finalize POST failed:', err);
          return {};
        });

        var sessionPromise = (window.InfinitSession && window.InfinitSession.isInitialized())
          ? window.InfinitSession.set('quiz_results', sessionPayload).catch(function (err) {
              console.warn('[quiz] InfinitSession.set failed:', err);
            })
          : Promise.resolve();

        Promise.all([finalizePromise, sessionPromise]).then(function (out) {
          var finalizeRes = out[0] || {};
          var token = finalizeRes.submission_token || '';
          // account_status drives the banner on the results page:
          //   'existing_linked' → "Log in to save your plan" banner
          //   'created_pending_activation' → "Activate your account" banner
          //   'logged_in' / 'guest_no_account' → no banner
          var accountStatus = finalizeRes.account_status || '';
          var qs = [];
          if (token) qs.push('submission=' + encodeURIComponent(token));
          if (accountStatus) qs.push('account=' + encodeURIComponent(accountStatus));
          var url = '/products/quiz-results' + (qs.length ? '?' + qs.join('&') : '');
          window.location.href = url;
        }).catch(function () {
          hideQuizWorkingOverlay();
          setActiveIndex(next);
          updateStepNav();
        });
        return;
      }

      hideQuizWorkingOverlay();
      setActiveIndex(next);
      updateStepNav();
    }).catch(function (err) {
      console.error('[quiz] Scoring failed, rendering with empty boosts:', err);

      // Fallback: score with no boosts so results still render
      if (window.QuizEngine && window.QuizEngine.score) {
        try {
          var fallback = window.QuizEngine.score(answers, [], []);
          renderScoredResults(fallback);
        } catch (e) {
          console.error('[quiz] Fallback scoring also failed:', e);
        }
      }

      hideQuizWorkingOverlay();
      setActiveIndex(next);
      updateStepNav();
    });
  });


  // Intro Start button: jump to the first available Step 0 panel
  if (startBtn) {
    startBtn.addEventListener('click', () => {
      const idx = firstAvailableIndexForStep(0);
      if (idx !== -1) setActiveIndex(idx);
    });
  }


  root.addEventListener('change', (e) => {
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('input[type="radio"], input[type="checkbox"], input[type="range"], input[type="text"], input[type="email"], input[type="date"], input[type="number"], textarea')) {
      if (target.matches('input[type="checkbox"]')) handleExclusiveCheckboxChange(target);
      evaluateDependencies();
      updateNextState();
    }
  });

  root.addEventListener('input', (e) => {
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('input[type="text"], input[type="email"], input[type="date"], input[type="number"], textarea, input[type="range"]')) {

      evaluateDependencies();
      updateNextState();
    }
  });


  evaluateDependencies();
  setActiveIndex(findFirstAvailableIndex());

  /* ----------------------------
   Slider visuals (copied from snippets/slider-standalone.liquid)
   Scoped to #infinit-quiz
   - Adds optional data-snap support for points like 0|33|66|100
  ----------------------------- */
  (() => {
    const wraps = document.querySelectorAll('#infinit-quiz .slider-wrap');

    wraps.forEach(wrap => {
      const input = wrap.querySelector('.protein-range');
      if (!input) return;

      /* Build the shaped rail once */
      let rail = wrap.querySelector('.rail');
      let inner = wrap.querySelector('.rail-inner');
      if (!rail) {
        rail = document.createElement('div');
        rail.className = 'rail';
        inner = document.createElement('div');
        inner.className = 'rail-inner';
        rail.appendChild(inner);
        wrap.insertBefore(rail, input);   // rail under the input
      }

      /* Visual thumb */
      let v = wrap.querySelector('.ui-thumb');
      if (!v) {
        v = document.createElement('span');
        v.className = 'ui-thumb';
        wrap.appendChild(v);
      }

      const setPercent = () => {
        const min = +input.min || 0;
        const max = +input.max || 100;
        const val = +input.value || 0;
        const pct = ((val - min) / (max - min)) * 100;
        wrap.style.setProperty('--percent', pct.toFixed(4));
        return pct;
      };

      const positionThumb = (pct) => {
        // Keep the visual handle fully inside the rail (no overhang left or right)
        const wrapRect  = wrap.getBoundingClientRect();
        const innerRect = inner.getBoundingClientRect();

        const cs = getComputedStyle(wrap);
        const capL   = parseFloat(cs.getPropertyValue('--cap-x')) || 0;  // left chamfer px
        const capR   = parseFloat(cs.getPropertyValue('--cap-r')) || 0;  // right chamfer px
        const thumbW = parseFloat(cs.getPropertyValue('--thumb-w')) || 0;
        const anchor = parseFloat(cs.getPropertyValue('--thumb-anchor')) || 0; // 0..1 from left

        // Overhangs of the image past its contact point
        const leftOverhang  = thumbW * anchor;
        const rightOverhang = thumbW * (1 - anchor);

        // Allowed range for the contact point, inside the visible inner rail
        const minContactX = innerRect.left  + Math.max(capL, leftOverhang);
        const maxContactX = innerRect.right - Math.max(capR, rightOverhang);

        // Desired position along the inner rail, then clamp to the allowed range
        const desiredContactX  = innerRect.left + (pct / 100) * innerRect.width;
        const clampedContactX  = Math.min(Math.max(desiredContactX, minContactX), maxContactX);

        // Write left in wrap-local pixels
        const leftPx = clampedContactX - wrapRect.left;
        wrap.style.setProperty('--thumb-left', `${leftPx}px`);
      };

      const sync = () => {
        // Snap discrete sliders (ex: step=50 => 0/50/100)
        const snapStep = +input.step || 1;
        if (snapStep > 1) {
          const min = +input.min || 0;
          const max = +input.max || 100;
          const v = +input.value || 0;

          const snapped = Math.round((v - min) / snapStep) * snapStep + min;
          const clamped = Math.min(max, Math.max(min, snapped));
          if (clamped !== v) input.value = clamped;
        }

        // Optional: snap to explicit points via data-snap="0|33|66|100"
        const snapAttr = input.getAttribute('data-snap');
        if (snapAttr) {
          const pts = snapAttr.split('|')
            .map(s => +String(s).trim())
            .filter(n => !Number.isNaN(n));

          if (pts.length) {
            const min = +input.min || 0;
            const max = +input.max || 100;
            const v = +input.value || 0;

            let best = pts[0];
            let bestDist = Math.abs(v - best);
            for (let i = 1; i < pts.length; i++) {
              const d = Math.abs(v - pts[i]);
              if (d < bestDist) { best = pts[i]; bestDist = d; }
            }

            const clamped = Math.min(max, Math.max(min, best));
            if (clamped !== v) input.value = clamped;
          }
        }

        const pct = setPercent();
        positionThumb(pct);
      };

      // value & layout changes
      input.addEventListener('input', () => { input.dataset.quizTouched = '1'; sync(); });
      input.addEventListener('change', () => { input.dataset.quizTouched = '1'; sync(); });
      window.addEventListener('resize', sync);
      new ResizeObserver(sync).observe(wrap);

      sync(); // initial paint
    });
  })();

})();
